import React, { useContext, useEffect } from 'react'
import styled, { css } from 'styled-components'
import GatsbyImage from 'gatsby-image'
import { useInView } from 'react-intersection-observer'
import { LayoutContext, useUI } from '../../contexts'
import {
  useCollectionClickedTracking,
  useModuleViewedTracking,
} from '../../hooks/tracking'
import { SiteLink } from '../shared/SiteLink'
import ArtDirectingImage from '../shared/ArtDirectingImage'
import { getAvailable } from '../../utils'

const TileBlock = ({ tileLinks, location, position }) => {
  const { node_locale, currency } = useContext(LayoutContext)

  // IE doesn't support intersection observer, breaks the site
  const isIE =
    /*@cc_on!@*/ false ||
    (typeof window !== `undefined` && !!document.documentMode)
  const [ref, inView] = isIE ? [] : useInView({ triggerOnce: true })

  const setTileLinksArray = (array) => {
    // this function groups the links into arrays of 4 for styling
    let tileLinksArray = []
    for (let i = 0; i < array.length; i += 4) {
      tileLinksArray.push(
        Array.from(
          new Set([array[i], array[i + 1], array[i + 2], array[i + 3]]),
        ),
      )
    }
    return tileLinksArray.map((array) => array.filter((i) => i))
  }

  useEffect(() => {
    if (!isIE) {
      if (inView) {
        useModuleViewedTracking({
          currency: currency?.code,
          locale: node_locale,
          sectionType: 'Tile Block',
          order: position,
        })
      }
    }
  }, [!isIE && inView])

  return (
    <StyledTileBlock ref={ref}>
      {setTileLinksArray(tileLinks.filter(getAvailable)).map((links, index) => (
        <GridContainer key={index}>
          {links?.length > 0
            ? links.map((link, index, array) => {
                const { title, target, linkExtension, image, mobileImage } =
                  link

                const ImageWrapperToRender = ({ children, ...props }) =>
                  link && target ? (
                    <ImageWrapperLink {...props}>{children}</ImageWrapperLink>
                  ) : (
                    <ImageWrapper {...props}>
                      {children} <p className='error-message'>No Link</p>
                    </ImageWrapper>
                  )

                return (
                  <ImageWrapperToRender
                    to={target?.slug}
                    extension={linkExtension}
                    islargeimagemobile={index === 0 || index === 3}
                    islargeimagedesktop={
                      array.length === 4
                        ? index === 0 || index === 3
                        : array.length === 3
                        ? index === 0
                        : array.length === 2
                        ? index === 0 || index === 1
                        : array.length === 1
                        ? index === 0
                        : null
                    }
                    position={index}
                    numofitems={array.length}
                    key={index}
                    onClick={() =>
                      useCollectionClickedTracking({
                        currency: currency?.code,
                        locale: node_locale,
                        name: target.title,
                        label: title,
                        linkType: 'Image',
                        viewType: 'Landing Page',
                        sectionType: 'Tile Block',
                        destination: `/${node_locale}/${target.slug}/`,
                      })
                    }
                    state={{ referrer: location.pathname }}
                  >
                    <LinkImage
                      fluid={ArtDirectingImage(
                        mobileImage?.fluid,
                        image?.fluid,
                      )}
                      alt={title}
                    />
                    <Title
                      islargeimagedesktop={
                        array.length === 4
                          ? index === 0 || index === 3
                          : array.length === 3
                          ? index === 0
                          : array.length === 2
                          ? index === 0 || index === 1
                          : array.length === 1
                          ? index === 0
                          : null
                      }
                    >
                      {title}
                    </Title>
                    <Overlay className='overlay' index={index} />
                  </ImageWrapperToRender>
                )
              })
            : null}
        </GridContainer>
      ))}
    </StyledTileBlock>
  )
}

const StyledTileBlock = styled.div`
  width: 95%;
  margin: 0 auto;
  padding: ${(props) => props.theme.space.marginMobile / 2}vw 0;
  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    width: 83%;
    padding: 0 0 ${(props) => props.theme.space.marginDesktop * 5}vw 0;
  }
`

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: ${(props) => props.theme.space.marginMobile / 2}vw;
  width: 100%;
  padding: ${(props) => props.theme.space.marginMobile / 4}vw 0;

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    grid-template-columns: repeat(2, 1fr);
    grid-template-rows: repeat(auto-fill, minmax(0, 1fr));
    grid-gap: ${(props) => props.theme.space.marginDesktop * 2}vw;
    padding: ${(props) => props.theme.space.marginDesktop}vw 0;
  }

  @media screen and (-ms-high-contrast: active) and (min-width: ${(props) =>
      props.theme.breakPoints.desktop}),
    screen and (-ms-high-contrast: none) and (min-width: ${(props) =>
      props.theme.breakPoints.desktop}) {
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;
  }
`

const imageWrapperStyles = css`
  height: 57.5vw;
  position: relative;
  width: 100%;
  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    height: ${(props) => (props.islargeimagedesktop ? '51vw' : '24.81vw')};
    grid-column: ${(props) => {
      switch (`${props.position} of ${props.numofitems}`) {
        case '0 of 4':
          return '1/2'
        case '1 of 4':
          return '1/2'
        case '2 of 4':
          return '2/3'
        case '3 of 4':
          return '2/3'
        case '0 of 3':
          return '1/2'
        case '1 of 3':
          return '2/3'
        case '2 of 3':
          return '2/3'
        case '0 of 2':
          return '1/2'
        case '1 of 2':
          return '2/3'
        case '0 of 1':
          return '1/2'
      }
    }};
    grid-row: ${(props) => {
      switch (`${props.position} of ${props.numofitems}`) {
        case '0 of 4':
          return '1/3'
        case '1 of 4':
          return '3/4'
        case '2 of 4':
          return '1/2'
        case '3 of 4':
          return '2/4'
        case '0 of 3':
          return '1/3'
        case '1 of 3':
          return '1/2'
        case '2 of 3':
          return '2/3'
        case '0 of 2':
          return '1/3'
        case '1 of 2':
          return '1/3'
        case '0 of 1':
          return '1/3'
      }
    }};
  }

  @media screen and (-ms-high-contrast: active) and (min-width: ${(props) =>
      props.theme.breakPoints.desktop}),
    screen and (-ms-high-contrast: none) and (min-width: ${(props) =>
      props.theme.breakPoints.desktop}) {
    height: 51vw;
    width: 40.5vw;
    padding: ${(props) => props.theme.space.marginDesktop}vw 0;
    overflow: hidden;
  }
`

const ImageWrapperLink = styled(SiteLink)`
  ${imageWrapperStyles}
`
const ImageWrapper = styled.div`
  ${imageWrapperStyles}
  .error-message {
    position: absolute;
    bottom: 0;
    color: white;
  }
`

const LinkImage = styled(GatsbyImage)`
  height: 100%;
  width: 100%;
  object-fit: cover;

  @media screen and (-ms-high-contrast: active) and (min-width: ${(props) =>
      props.theme.breakPoints.desktop}),
    screen and (-ms-high-contrast: none) and (min-width: ${(props) =>
      props.theme.breakPoints.desktop}) {
    img {
      width: auto !important;
      height: 120% !important;
      position: absolute;
      top: 50% !important;
      left: 50% !important;
      transform: translate(-50%, -50%);
    }
  }
`

const Title = styled.span`
  color: #fff;
  font-size: 7vw;
  margin: 0 auto;
  z-index: 15;
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  bottom: ${(props) => props.theme.space.marginMobile * 2.5}vw;
  text-align: center;
  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    font-size: 3.5vw;
    bottom: ${(props) =>
      props.islargeimagedesktop
        ? props.theme.space.marginDesktop * 10
        : props.theme.space.marginDesktop * 5}vw;
  }
  @media screen and (-ms-high-contrast: active) and (min-width: ${(props) =>
      props.theme.breakPoints.desktop}),
    screen and (-ms-high-contrast: none) and (min-width: ${(props) =>
      props.theme.breakPoints.desktop}) {
    bottom: ${(props) => props.theme.space.marginDesktop * 10}vw;
  }
`

const Overlay = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  z-index: 10;
  background-image: linear-gradient(
    rgba(0, 0, 0, 0) 0%,
    rgba(0, 0, 0, 0.15) 90% 100%
  );
`

export default TileBlock
